"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Insight = exports.InsightOptions = void 0;
const url_1 = require("./helpers/url");
const Environment_1 = require("./enums/Environment");
const basin_1 = require("./basin");
/**
 * The main options used when instantiating an instance of the Insight User SDK.
 *
 * @export
 * @class InsightOptions
 */
class InsightOptions {
}
exports.InsightOptions = InsightOptions;
/**
 * The main Insight User SDK class.  Instantiated with a valid API token and options such
 * as which environment to use and any endpoint overrides desired.
 *
 * @export
 * @class Insight
 */
class Insight {
    /**
     * Creates an instance of the Insight User SDK.  If the environment is not specified it will
     * default to Production, and if the locale isn't specified it will default to Japanese.
     * @param {InsightOptions} options
     * @memberof Insight
     */
    constructor(options) {
        var _a, _b;
        this.user = new (class {
            constructor(parentThis) {
                this.parentThis = parentThis;
            }
            /**
             Creates a particular user
             *
             * @return {*}  {Promise<boolean>}
             * @memberof Insight
             */
            async create() {
                return await this.parentThis._basin.createUser();
            }
        })(this);
        this.boards = new (class {
            constructor(parentThis) {
                this.parentThis = parentThis;
            }
            /**
             * Returns a user marketboard for a particular user
             *
             * @param {boardId} boardId
             * @return {*}  {Promise<ApiResponse<MarketboardResponse>>}
             * @memberof Insight
             */
            async get(boardId) {
                return await this.parentThis._basin.getMarketboard(boardId);
            }
            /**
             * Returns a list of user marketboards for a particular user
             *
             * @param {format?} format
             * @param {number?} top
             * @param {number?} skip
             * @return {*}  {Promise<ApiResponse<MarketboardsResponse>>}
             * @memberof Insight
             */
            async getAll(format, top, skip) {
                return await this.parentThis._basin.getMarketboards(format, top, skip);
            }
            /**
             * Adds a new user marketboard for a particular user
             *
             * @param {marketboard} marketboard
             * @return {*}  {Promise<ApiResponse<MarketboardResponse>>}
             * @memberof Insight
             */
            async add(marketboard) {
                return await this.parentThis._basin.addMarketboard(marketboard);
            }
            /**
             * Updates a user marketboard for a particular user
             *
             * @param {boardId} boardId
             * @param {marketboard} marketboard
             * @return {*}  {Promise<ApiResponse<MarketboardResponse>>}
             * @memberof Insight
             */
            async update(boardId, marketboard) {
                return await this.parentThis._basin.updateMarketboard(boardId, marketboard);
            }
            /**
             * Saves a list of user marketboards for a particular user
             *
             * @param {Marketboard[]} marketboards
             * @param {number?} marketboardLastUpdate
             * @return {*}  {Promise<MarketboardsResponse>}
             * @memberof Insight
             */
            async replaceAll(marketboards, marketboardLastUpdate) {
                return await this.parentThis._basin.saveMarketboards(marketboards, marketboardLastUpdate);
            }
            /**
             * Deletes a user marketboard for a particular user
             *
             * @param {boardId} boardId
             * @return {*}  {Promise<any>}
             * @memberof Insight
             */
            async delete(boardId) {
                await this.parentThis._basin.deleteMarketboard(boardId);
            }
        })(this);
        this.scans = new (class {
            constructor(parentThis) {
                this.parentThis = parentThis;
            }
            /**
             * Gets a list of user scans for a particular user
             *
             * @return {*}  {Promise<Scans>}
             * @memberof Insight
             */
            async getAll() {
                return await this.parentThis._basin.getScans();
            }
            /**
             * Replaces the user scans for a particular user
             *
             * @param {Scan[]} scans
             * @return {*}  {Promise<void>}
             * @memberof Insight
             */
            async replaceAll(scans) {
                await this.parentThis._basin.replaceScans(scans);
            }
            /**
             * Adds a user scan for a particular user
             *
             * @param {Scan} scan
             * @return {*}  {Promise<void>}
             * @memberof Insight
             */
            async add(scan) {
                return await this.parentThis._basin.addScan(scan);
            }
            /**
             * Gets a single scan object from the user's list of saved scans
             *
             * @param {string} scanId
             * @return {*}  {Promise<Scan>}
             * @memberof Insight
             */
            async getById(scanId) {
                return await this.parentThis._basin.getScan(scanId);
            }
            /**
             * Gets a single scan object from the user's list of saved scans
             *
             * @param {string} scanId
             * @param {Scan} scan
             * @return {*}  {Promise<void>}
             * @memberof Insight
             */
            async replaceById(scanId, scan) {
                await this.parentThis._basin.replaceScan(scanId, scan);
            }
            /**
             * Deletes a single scan object in the user's list of saved scans
             *
             * @param {string} scanId
             * @return {*}  {Promise<void>}
             * @memberof Insight
             */
            async deleteById(scanId) {
                await this.parentThis._basin.deleteScan(scanId);
            }
        })(this);
        this.memos = new (class {
            constructor(parentThis) {
                this.parentThis = parentThis;
            }
            /**
             * Gets a list of user memos for a particular user
             *
             * @return {*}  {Promise<Memo[]>}
             * @memberof Insight
             */
            async getAll() {
                return await this.parentThis._basin.getMemos();
            }
            /**
             * Adds a user memo for the specific symbolId
             *
             * @param {string} symbolId
             * @param {string} memo
             * @return {*}  {Promise<MemoPost>}
             * @memberof Insight
             */
            async addBySymbol(symbolId, memo) {
                return await this.parentThis._basin.addMemoBySymbol(symbolId, memo);
            }
            /**
             * Gets a single Memo object (If found) for the specific symbolId
             *
             * @param {string} symbolId
             * @return {*}  {Promise<Memo|undefined>}
             * @memberof Insight
             */
            async getBySymbol(symbolId) {
                return await this.parentThis._basin.getMemoBySymbol(symbolId);
            }
            /**
             * Gets an array of Memo objects for the supplied symbols. Maximum of 10 symbols
             *
             * @param {Array<string>} symbolIds
             * @return {*}  {Promise<Memo[]>}
             * @memberof Insight
             */
            async getBySymbols(symbolIds) {
                return await this.parentThis._basin.getMemosBySymbols(symbolIds);
            }
            /**
             * Deletes a single Memo object for the specific symbolId
             *
             * @param {string} symbolId
             * @return {*}  {Promise<void>}
             * @memberof Insight
             */
            async deleteBySymbol(symbolId) {
                await this.parentThis._basin.deleteMemoBySymbol(symbolId);
            }
        })(this);
        this.positions = new (class {
            constructor(parentThis) {
                this.parentThis = parentThis;
            }
            /**
             * Gets a user's posistions
             *
             * @param {boolean} detailed
             * @return {*}  {Promise<Positions>}
             * @memberof Insight
             */
            async getAll(detailed = false) {
                return await this.parentThis._basin.getPositions(detailed);
            }
        })(this);
        // Require a token to create the SDK, as only Syft will work
        // without one.
        if (!options.token) {
            throw new Error('TOKEN_MISSING');
        }
        this._token = options.token;
        // Load in the specified values, otherwise load in the defaults
        this._environment = (_a = options.environment) !== null && _a !== void 0 ? _a : Environment_1.Environment.Production;
        // Load in any specified overrides for different services
        this._apiUrlOverride = (_b = options.apiUrlOverride) !== null && _b !== void 0 ? _b : null;
        // monexSubdomain and/or encryptedUserId can be null, but are
        // required to get positions from Basin
        this._basin = new basin_1.BasinClient({
            apiUrl: this._getApiUrl(),
            token: this._token,
            monexSubdomain: options.monexSubdomain,
            encryptedUserId: options.encryptedUserId
        });
    }
    /**
     * Gets the base API Gateway URL for the environment specified when instantiating the SDK.
     * Used as the base for Basin and Messages.
     *
     * @private
     * @return {*}  Base URL of the API Gateway for the current environment.
     * @memberof Insight
     */
    _getApiUrl() {
        // Always return the overriden API URL if it was provided
        if (this._apiUrlOverride) {
            return this._apiUrlOverride;
        }
        const domain = (0, url_1.getDomainNameForEnvironment)(this._environment);
        return `api.${domain}`;
    }
}
exports.Insight = Insight;
// Since the following code is to be used only for browser and the window
// property cannot be easily mocked when executing the tests in nodejs,
// the following comment was added so it will be ignored in the test
// coverage
/* istanbul ignore next */
if (typeof window === 'object') {
    window.Insight = Insight;
}
//# sourceMappingURL=insight.js.map